import styled, { CSSObject } from '@emotion/styled'
import { useModal, useShop, useFulfillment } from 'shop/hooks'
import { StyledHTMLElement } from 'shop/theme/types'
import { FulfillmentTime } from '../../shared/FulfillmentTime/FulfillmentTime'
import { IoChevronForward } from 'react-icons/io5'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ContentLoader from 'react-content-loader'

const FulfillmentSummary = () => {
  const { openModal, isModalOpen, fulfillmentModal, closeModal } = useModal()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentPartnerStore, isPartnerLoading } = useShop()
  const {
    fulfillmentModalEnabled,
    fulfillmentWindow,
    orderType,
    isOat,
    isStoreAcceptingOrders
  } = useFulfillment()
  const [shouldShowFulfillmentModal, setShouldShowFulfillmentModal] =
    useState(false)

  const openFulfilmentModal = () => {
    if (!isModalOpen('fulfillment')) {
      setShouldShowFulfillmentModal(true)
      openModal('fulfillment')
    }
  }

  const handleFulfillmentModalClose = () => {
    setShouldShowFulfillmentModal(false)
    closeModal('fulfillment')
  }

  useEffect(() => {
    const queryParam = new URLSearchParams(location.search)
    // If fulfillment query param from login modal flow exists, remove it
    if (queryParam.get('fulfillment') && !isModalOpen('fulfillment')) {
      setShouldShowFulfillmentModal(true)
      openModal('fulfillment')
      navigate({
        pathname: location.pathname,
        search: queryParam.toString()
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isLoadingInfo = useMemo(
    () => isPartnerLoading || !currentPartnerStore,
    [isPartnerLoading, currentPartnerStore]
  )

  if (isLoadingInfo) {
    // Loading state whilst Partner data is updated
    return (
      <Root data-testid='fulfillment-summary'>
        <Container>
          <ContentLoader
            style={{
              width: '96%',
              height: '20px',
              borderRadius: '4px'
            }}
          />
        </Container>
      </Root>
    )
  }

  return (
    <Root data-testid='fulfillment-summary'>
      {isModalOpen('fulfillment') &&
        shouldShowFulfillmentModal &&
        fulfillmentModal({
          onClose: handleFulfillmentModalClose
        })}

      {isOat ? (
        <></>
      ) : (
        <Container
          onClick={fulfillmentModalEnabled ? openFulfilmentModal : undefined}
        >
          <Info>
            <Row data-testid='fulfillment-summary-row'>
              {!isStoreAcceptingOrders ? (
                <Line>Currently not accepting orders</Line>
              ) : (
                <>
                  {fulfillmentWindow && (
                    <Line>
                      <FulfillmentTime
                        fulfillmentWindow={fulfillmentWindow}
                        orderType={orderType}
                      />
                    </Line>
                  )}
                </>
              )}
            </Row>
          </Info>
          {fulfillmentModalEnabled && (
            <ChevronContainer>
              <IoChevronForward />
            </ChevronContainer>
          )}
        </Container>
      )}
    </Root>
  )
}

const Root = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  margin: 'auto 0',
  right: '12px',
  bottom: '27px',
  background: 'white',
  borderRadius: theme.borderRadius,
  width: '100%',
  cursor: 'pointer',
  [theme.mediaQueries.viewport4]: {
    right: '27px',
    bottom: '29px'
  },
  [theme.mediaQueries.viewport7]: {
    position: 'relative',
    right: 'auto',
    bottom: 'auto'
  }
}))

const Info = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    flexFlow: 'column',
    lineHeight: '18px'
  })
)

const Container = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    width: '100%',
    gap: '5px'
  })
)

export const Row = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'block'
  })
)

const Line = styled.span<StyledHTMLElement>(() => ({
  fontSize: '14px',
  margin: 0,
  color: '#59595A',
  height: '20px',
  b: {
    color: 'black'
  }
}))

const ChevronContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    padding: '3px 0 0 0',
    color: '#59595A',
    marginLeft: 'auto'
  })
)

export default FulfillmentSummary
